import { useMutation } from '@platform/api-client';
import { Button, MoneyText, Textarea } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { Truck } from 'lucide-react';
import { EntryList } from '../EntryList.tsx';
import type { Geography } from '../geography/useGeoAreas.ts';
import { usePageContext } from '../usePageContext.ts';
import { DeliveryFields } from './DeliveryFields.tsx';
import {
  amendBodyOf,
  ORDER_FORM_LIMITS,
  orderFormValuesOf,
  type NewOrderFormValues,
} from './new-order-form.ts';
import styles from './NewOrder.module.css';
import { useOrderForm } from './OrderForm.tsx';
import { amountOf, STOCK_RESOURCES, type Order } from './orders.ts';

export type CarrierDetailsFormProps = {
  api: TypedClient;
  order: Order;
  carrierName: string;
  geography: Geography;
  onDone: (orderId: string) => void;
};

export const CarrierDetailsForm = ({
  api,
  order,
  carrierName,
  geography,
  onDone,
}: CarrierDetailsFormProps) => {
  const { t, locale, notify, describeError, invalidate } = usePageContext();
  const form = useOrderForm(orderFormValuesOf(order));
  const save = useMutation({
    mutationFn: (values: NewOrderFormValues) =>
      api.patch('/admin/orders/{id}', { path: { id: order.id }, body: amendBodyOf(order.version, values) }),
    onSuccess: async (saved) => {
      notify(t('editOrder.savedAtCarrier', { number: saved.number, carrier: carrierName }));
      await invalidate(...STOCK_RESOURCES);
      onDone(saved.id);
    },
  });

  return (
    <form
      noValidate
      className={styles['page']}
      onSubmit={(event) => {
        void form.handleSubmit((values) => {
          save.mutate(values);
        })(event);
      }}
    >
      <div className={styles['layout']}>
        <div className={styles['main']}>
          <p className={styles['notice']} role="note">
            <Truck size={20} aria-hidden="true" />
            <span>{t('editOrder.atCarrierHelp', { carrier: carrierName })}</span>
          </p>
          <section className={styles['section']} aria-labelledby="order-customer">
            <h2 id="order-customer">{t('newOrder.customerSection')}</h2>
            <EntryList
              labelOf={(key) => t(`orders.${key}`)}
              entries={[
                { key: 'customer', value: order.customer.name },
                { key: 'phone', value: order.customer.phone, ltr: true },
              ]}
            />
          </section>
          <section className={styles['section']} aria-labelledby="order-delivery">
            <h2 id="order-delivery">{t('newOrder.deliverySection')}</h2>
            <DeliveryFields form={form} geography={geography} />
            <Textarea
              label={t('orders.note')}
              rows={2}
              maxLength={ORDER_FORM_LIMITS.note}
              {...form.register('note')}
            />
          </section>
          <section className={styles['section']} aria-labelledby="order-items">
            <h2 id="order-items">{t('editOrder.lockedItems')}</h2>
            <ul className={styles['lockedLines']}>
              {order.lines.map((line) => (
                <li key={line.variantId}>
                  <span className={styles['lineName']}>
                    {line.productName}
                    <span className={styles['muted']}>{line.sku}</span>
                  </span>
                  <span>{t('orders.itemCount', { count: line.quantity })}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
        <aside className={styles['summary']} aria-labelledby="order-summary">
          <h2 id="order-summary">{t('newOrder.summarySection')}</h2>
          <dl className={styles['totals']}>
            <div className={styles['total']}>
              <dt>{t('orders.codAmount')}</dt>
              <dd className={styles['amount']}>
                <MoneyText money={amountOf(order, order.totals.codAmount)} locale={locale} />
              </dd>
            </div>
          </dl>
          {save.isError ? (
            <p className={styles['error']} role="alert">
              {describeError(save.error)}
            </p>
          ) : null}
          <div className={styles['submit']}>
            <Button
              variant="ghost"
              onClick={() => {
                onDone(order.id);
              }}
            >
              {t('editOrder.discard')}
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending
                ? t('editOrder.saving')
                : t('editOrder.sendToCarrier', { carrier: carrierName })}
            </Button>
          </div>
        </aside>
      </div>
    </form>
  );
};
